import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Paper,
  IconButton,
  TextField,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/EditRounded";
import AddTaskRoundedIcon from "@mui/icons-material/AddTaskRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteIcon from "@mui/icons-material/DeleteRounded";
import OpenInNewIcon from "@mui/icons-material/OpenInNewRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { useStateContext } from "contexts/ContextProvider";
import { useWebSocket } from "contexts/WebSocketContext";
import Grid from "@mui/material/Grid2";
import { useAuth } from "contexts/AuthContext";
import TasksFilterSelector from "components/TasksFilterSelector";
import TaskItem from "components/TaskItem";
import { OVERLAY_NAMES } from "constants/Overlays";
import api from "services/api";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { usePageTitle } from "hooks/usePageTitle";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import { NAVBAR_HEIGHT } from "constants/layout";
import { FILTER_SELECTOR_WIDTH } from "constants/filterSelectors";

export const buildTaskQueryParams = (filters, currentUserId) => {
  const params = new URLSearchParams();

  if (filters?.dueDates?.length) {
    filters.dueDates.forEach((dueDate) =>
      params.append("dueDateFilter", dueDate),
    );
  }

  if (filters?.priorities?.length) {
    filters.priorities.forEach((priority) =>
      params.append("priority", priority),
    );
  }

  if (filters?.entityServiceTypes?.length) {
    filters.entityServiceTypes.forEach((type) => {
      if (
        type === "Interpretation" ||
        type === "Translation" ||
        type === "Other Service"
      ) {
        params.append("serviceType", type);
        params.append("entityType", "Service");
      } else {
        params.append("entityType", type);
      }
    });
  }

  if (filters?.showAllCompleted) {
    params.append("showAllCompleted", true);
  }

  if (filters?.showAutomatedTasks === false) {
    params.append("hideAutomatedTasks", true);
  }

  if (filters?.selectedMember === "me" && currentUserId) {
    params.append("memberId", currentUserId);
  }

  return params;
};

export const fetchTasksData = async ({ filters, currentUserId }) => {
  const params = buildTaskQueryParams(filters, currentUserId);
  const response = await api.get(`/tasks?${params.toString()}`);
  return response.data;
};

const StatusColumn = ({
  status,
  tasks,
  statuses,
  theme,
  renderTaskCard,
  creatingTaskForStatus,
  newTaskTitle,
  setNewTaskTitle,
  handleCreateInlineTask,
  setCreatingTaskForStatus,
  enableInlineCreate = true,
  columnsOverride,
  heightFraction,
  rowHeightFraction,
  gridSpacing = 2,
  constrainToParentHeight = false,
}) => {
  const statusTasks = tasks.filter((task) => task.Status === status);
  const colCount = columnsOverride || statuses.length || 1;
  const mdCols = Math.max(1, Math.min(12, Math.floor(12 / colCount)));

  return (
    <Grid
      size={{ xs: 12, sm: 6, md: mdCols }}
      key={status}
      sx={{
        height:
          rowHeightFraction && constrainToParentHeight && rowHeightFraction < 1
            ? `calc(${Math.round(rowHeightFraction * 100)}% - ${theme.spacing(
                gridSpacing / 2,
              )})`
            : rowHeightFraction
              ? `${Math.round(rowHeightFraction * 100)}%`
              : "100%",
      }}
    >
      <Paper
        sx={{
          p: 2,
          bgcolor: theme.palette.background.default,
          borderRadius: "12px",
          height: heightFraction
            ? `${Math.round(heightFraction * 100)}%`
            : "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          boxShadow: theme.shadows[3],
        }}
      >
        <Box
          sx={{
            mb: 2,
            pb: 1,
            borderBottom: `2px solid ${theme.palette.divider}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: "bold",
              color: theme.palette.primary.text,
            }}
          >
            {status}
          </Typography>
          <Box
            sx={{
              minWidth: 32,
              height: 28,
              borderRadius: "12px",
              bgcolor: theme.palette.background.alt,
              color: theme.palette.primary.text2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 600,
              fontSize: "0.8rem",
            }}
          >
            {statusTasks.length}
          </Box>
        </Box>

        <Droppable
          droppableId={status || "unknown-status"}
          type="TASK"
          direction="vertical"
          ignoreContainerClipping
          isDropDisabled={false}
          isCombineEnabled={false}
        >
          {(provided, snapshot) => (
            <Box
              ref={provided.innerRef}
              {...provided.droppableProps}
              style={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                minHeight: "100px",
                boxSizing: "border-box",
                overflowX: "visible",
              }}
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                maxWidth: "100%",
                minWidth: 0,
                backgroundColor: snapshot.isDraggingOver
                  ? theme.palette.action.hover
                  : "transparent",
                borderRadius: "12px",
                padding: "4px",
                transition: "background-color 0.2s ease",
              }}
            >
              {Array.isArray(statusTasks) &&
                statusTasks.map((task, index) => renderTaskCard(task, index))}
              {provided.placeholder &&
                React.cloneElement(provided.placeholder, {
                  style: {
                    ...provided.placeholder.props.style,
                    width: "100%",
                    marginBottom: "12px",
                    flexShrink: 0,
                  },
                })}
            </Box>
          )}
        </Droppable>

        {enableInlineCreate && (
          <Box sx={{ mt: 1.5, px: 0.5 }}>
            {creatingTaskForStatus === status ? (
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <TextField
                  size="small"
                  placeholder="Task title..."
                  value={newTaskTitle}
                  onChange={(e) => setNewTaskTitle(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleCreateInlineTask(status);
                    }
                  }}
                  autoFocus
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: theme.palette.background.alt,
                    },
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => handleCreateInlineTask(status)}
                  sx={{
                    bgcolor: theme.palette.primary.main,
                    color: "white",
                    borderRadius: "12px",
                    "&:hover": {
                      bgcolor: theme.palette.primary.buttonHover,
                    },
                  }}
                >
                  <AddTaskRoundedIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => {
                    setCreatingTaskForStatus(null);
                    setNewTaskTitle("");
                  }}
                  sx={{
                    color: theme.palette.primary.main,
                  }}
                >
                  <CloseRoundedIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <IconButton
                size="small"
                onClick={() => setCreatingTaskForStatus(status)}
                sx={{
                  color: theme.palette.primary.main,
                  width: "100%",
                  borderRadius: "12px",

                  "&:hover": {
                    bgcolor: theme.palette.action.hover,
                  },
                }}
              >
                <AddRoundedIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        )}
      </Paper>
    </Grid>
  );
};

export const TaskBoard = ({
  statuses,
  tasks,
  isLoading,
  onDragEnd,
  renderTaskCard,
  creatingTaskForStatus,
  newTaskTitle,
  setNewTaskTitle,
  handleCreateInlineTask,
  setCreatingTaskForStatus,
  theme,
  enableInlineCreate = true,
  columnsOverride,
  heightFraction,
  rowHeightFraction,
  containerPadding = ".75rem 0.25rem",
  containerMargin = "0 0 0 0",
  gridSpacing = 2,
  externalDragContext = false,
  constrainToParentHeight = false,
}) => {
  const content = (
    <>
      <Box
        sx={{
          flex: 1,
          overflowY: constrainToParentHeight ? "hidden" : "auto",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
          //margin: containerMargin,
          //pb: !containerPadding ? 1 : ".75rem", ////Same Padding
          //px: !containerPadding ? 1 : "0.25rem", //Same Padding
          //pt: !containerPadding ? 2 : ".75rem", //Same Padding
          p: containerPadding,
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ color: theme.palette.primary.text }}>
              Loading tasks...
            </Typography>
          </Box>
        ) : (
          <Grid
            container
            spacing={gridSpacing}
            sx={{ height: "100%", minHeight: 0, alignItems: "stretch" }}
          >
            {Array.isArray(statuses) &&
              statuses.map((status) => (
                <StatusColumn
                  key={status}
                  status={status}
                  tasks={tasks}
                  statuses={statuses}
                  theme={theme}
                  renderTaskCard={renderTaskCard}
                  creatingTaskForStatus={creatingTaskForStatus}
                  newTaskTitle={newTaskTitle}
                  setNewTaskTitle={setNewTaskTitle}
                  handleCreateInlineTask={handleCreateInlineTask}
                  setCreatingTaskForStatus={setCreatingTaskForStatus}
                  enableInlineCreate={enableInlineCreate}
                  columnsOverride={columnsOverride}
                  heightFraction={heightFraction}
                  rowHeightFraction={rowHeightFraction}
                  gridSpacing={gridSpacing}
                  constrainToParentHeight={constrainToParentHeight}
                />
              ))}
          </Grid>
        )}
      </Box>
    </>
  );

  return externalDragContext ? (
    content
  ) : (
    <DragDropContext onDragEnd={onDragEnd}>{content}</DragDropContext>
  );
};

const Tasks = () => {
  usePageTitle("Tasks");

  const { currentUser } = useAuth();
  const { socket } = useWebSocket();
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const navigate = useNavigate();
  const {
    refreshPage,
    setOverlayName,
    setOverlayData,
    showSnackbar,
  } = useStateContext();

  // State variables
  const [isLoading, setIsLoading] = useState(true);
  const [tasks, setTasks] = useState([]);

  // Enum states
  const [priorities, setPriorities] = useState([]);
  const [entityServiceTypes] = useState([
    "Interpretation",
    "Translation",
    "Other Service",
    "Client",
    "Linguist",
    "Invoice",
  ]);
  const [statuses, setStatuses] = useState([]);

  // Load filters from localStorage or use defaults
  const getInitialFilters = () => {
    try {
      const savedFilters = localStorage.getItem("taskFilters");
      if (savedFilters) {
        return JSON.parse(savedFilters);
      }
    } catch (error) {
      console.error("Error loading saved filters:", error);
    }
    return {
      dueDates: [],
      priorities: [],
      entityServiceTypes: [],
      showAllCompleted: false,
      showAutomatedTasks: true,
      selectedMember: "all",
    };
  };

  // Filter state - defaults: nothing checked, fetch all tasks
  const [filters, setFilters] = useState(getInitialFilters());

  const { openDialog, handleErrorDialog, closeDialog } = useStateContext();

  // Inline task creator state
  const [creatingTaskForStatus, setCreatingTaskForStatus] = useState(null);
  const [newTaskTitle, setNewTaskTitle] = useState("");

  // Context menu state
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedTaskForContext, setSelectedTaskForContext] = useState(null);

  useEffect(() => {
    const fetchEnums = async () => {
      try {
        const [prioritiesRes, statusesRes] = await Promise.all([
          api.get("/tasks/enums/priorities"),
          api.get("/tasks/enums/statuses"),
        ]);

        setPriorities(prioritiesRes.data);
        setStatuses(statusesRes.data);
      } catch (error) {
        console.error("Error fetching enums:", error);
        handleErrorDialog("Failed to load filter options", theme);
      }
    };

    fetchEnums();
  }, [handleErrorDialog, theme]);

  // Fetch tasks
  useEffect(() => {
    const fetchTasks = async () => {
      setIsLoading(true);
      try {
        const data = await fetchTasksData({
          filters,
          currentUserId: currentUser?.id,
        });
        setTasks(data);
      } catch (error) {
        console.error("Error fetching tasks:", error);
        handleErrorDialog("Failed to load tasks", theme);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTasks();
  }, [refreshPage, filters, currentUser?.id, handleErrorDialog, theme]);

  // Listen for task updates
  useEffect(() => {
    if (!socket) return;

    const handleTaskUpdate = async (data) => {
      // Refetch tasks to get updated list
      try {
        const updatedTasks = await fetchTasksData({
          filters,
          currentUserId: currentUser?.id,
        });
        setTasks(updatedTasks);

        // Show snackbar notification
        const userName = data.updatedBy?.userName || "Unknown User";
        const message = `Task ${data.changeAction} by ${userName}`;
        showSnackbar(message, theme, "info");
      } catch (error) {
        console.error("Error refreshing tasks:", error);
      }
    };

    socket.on("task:updated", handleTaskUpdate);
    return () => socket.off("task:updated", handleTaskUpdate);
  }, [socket, filters, currentUser?.id, showSnackbar, theme]);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    // Save to localStorage
    try {
      localStorage.setItem("taskFilters", JSON.stringify(newFilters));
    } catch (error) {
      console.error("Error saving filters:", error);
    }
  };

  const groupTasksByStatus = (taskList) => {
    const grouped = {};
    statuses.forEach((status) => {
      grouped[status] = [];
    });

    taskList.forEach((task) => {
      if (!grouped[task.Status]) {
        grouped[task.Status] = [];
      }
      grouped[task.Status].push(task);
    });

    return grouped;
  };

  const flattenTasksByStatus = (groupedTasks) => {
    const ordered = [];

    statuses.forEach((status) => {
      if (groupedTasks[status]) {
        ordered.push(...groupedTasks[status]);
      }
    });

    Object.keys(groupedTasks).forEach((status) => {
      if (!statuses.includes(status)) {
        ordered.push(...groupedTasks[status]);
      }
    });

    return ordered;
  };

  const handleDragEnd = async (result) => {
    const { destination, draggableId } = result;

    // Dropped outside the list
    if (!destination) {
      return;
    }

    const newStatus = destination.droppableId;
    // Extract task ID from draggableId (remove "task-" prefix)
    const taskId = draggableId.replace("task-", "");

    // Update UI optimistically - place task where it was dropped
    setTasks((prevTasks) => {
      const taskToMove = prevTasks.find(
        (task) => task.id.toString() === taskId,
      );
      if (!taskToMove) return prevTasks;

      const withoutTask = prevTasks.filter(
        (task) => task.id.toString() !== taskId,
      );
      const tasksByStatus = groupTasksByStatus(withoutTask);

      const updatedTask = { ...taskToMove, Status: newStatus };
      const destinationList = tasksByStatus[newStatus] || [];
      const insertAt = Math.min(destination.index, destinationList.length);
      destinationList.splice(insertAt, 0, updatedTask);
      tasksByStatus[newStatus] = destinationList;

      return flattenTasksByStatus(tasksByStatus);
    });

    // Update on server in background
    try {
      await api.patch(`/tasks/updateStatus/${taskId}`, {
        Status: newStatus,
      });

      // Emit socket event for real-time updates
      if (socket) {
        const taskToUpdate = tasks.find((t) => t.id.toString() === taskId);
        if (taskToUpdate) {
          socket.emit("task:saved", {
            modelType: "Task",
            recordId: parseInt(taskId),
            entityType: taskToUpdate.EntityType,
            entityID: taskToUpdate.EntityID,
            serviceType: taskToUpdate.ServiceType,
            changeAction: "Updated",
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });
        }
      }
    } catch (error) {
      console.error("Error updating task status:", error);
      handleErrorDialog("Failed to update task status", theme);

      // Revert optimistic update by refetching
      const params = new URLSearchParams();
      const response = await api.get(`/tasks?${params.toString()}`);
      setTasks(response.data);
    }
  };

  // Open task overlay for viewing
  const handleTaskClick = (task) => {
    setOverlayData({
      Task: task,
    });
    setOverlayName(OVERLAY_NAMES.TASK_PROFILE);
  };

  // Handle context menu
  const handleContextMenu = (event, task) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedTaskForContext(task);
    setContextMenu(
      contextMenu === null
        ? { mouseX: event.clientX - 2, mouseY: event.clientY - 4 }
        : null,
    );
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleEditFromContext = () => {
    if (selectedTaskForContext) {
      setOverlayData({
        Action: OVERLAY_NAMES.TASK_ACTION_EDIT,
        Task: selectedTaskForContext,
      });
      setOverlayName(OVERLAY_NAMES.TASK_NEW_EDIT);
    }
    handleCloseContextMenu();
  };

  const handleDeleteTask = async () => {
    if (!selectedTaskForContext) {
      handleCloseContextMenu();
      return;
    }

    openDialog({
      theme: theme,
      title: "Delete Task",
      message: `Are you sure you want to delete the task "${selectedTaskForContext.Title}"? This action cannot be undone.`,
      primaryAction: "Delete",
      secondaryAction: "Cancel",
      primaryOnClick: async () => {
        try {
          await api.delete(`/tasks/delete/${selectedTaskForContext.id}`);

          // Emit socket event for real-time updates
          if (socket) {
            socket.emit("task:saved", {
              modelType: "Task",
              recordId: selectedTaskForContext.id,
              entityType: selectedTaskForContext.EntityType,
              entityID: selectedTaskForContext.EntityID,
              serviceType: selectedTaskForContext.ServiceType,
              changeAction: "Deleted",
              user: {
                id: currentUser.id,
                name: currentUser.Name,
                imageThumbnail: currentUser.ImageThumbnail,
              },
            });
          }

          setTasks((prevTasks) =>
            prevTasks.filter((task) => task.id !== selectedTaskForContext.id),
          );
          closeDialog();
          handleCloseContextMenu();
        } catch (error) {
          console.error("Error deleting task:", error);
          closeDialog();
          handleErrorDialog("Failed to delete task", theme);
        }
      },
      secondaryOnClick: closeDialog,
    });

    handleCloseContextMenu();
  };

  const handleOpenEntityProfile = () => {
    if (
      !selectedTaskForContext ||
      !selectedTaskForContext.EntityType ||
      !selectedTaskForContext.EntityID
    ) {
      handleCloseContextMenu();
      return;
    }

    const { EntityType, EntityID, ServiceType } = selectedTaskForContext;
    let path = "";

    switch (EntityType) {
      case "Invoice":
        path = `/invoices/${EntityID}`;
        break;
      case "Client":
        path = `/clients/${EntityID}`;
        break;
      case "Linguist":
        path = `/linguists/${EntityID}`;
        break;
      case "Service":
        if (ServiceType === "Interpretation") {
          path = `/interpretations/${EntityID}`;
        } else if (ServiceType === "Translation") {
          path = `/translations/${EntityID}`;
        } else if (ServiceType === "Other Service") {
          path = `/otherServices/${EntityID}`;
        }
        break;
      default:
        console.error("Unknown EntityType:", EntityType);
        handleCloseContextMenu();
        return;
    }

    if (path) {
      navigate(path, { state: { ComingFrom: "/tasks" } });
    }
    handleCloseContextMenu();
  };

  const getEntityLabel = (entityType) => {
    if (entityType === "Service") return "Service";
    return entityType; // Client, Linguist, Invoice
  };

  const handleCreateInlineTask = async (status) => {
    if (!newTaskTitle.trim()) return;

    try {
      const response = await api.post("/tasks/create", {
        Title: newTaskTitle,
        Status: status,
        Priority: "Normal",
        CreatedByUserID: currentUser.id,
        AssignedToUserID: currentUser.id,
      });

      setNewTaskTitle("");
      setCreatingTaskForStatus(null);

      const createdTask = response.data;

      // Emit socket event for real-time updates
      if (socket) {
        socket.emit("task:saved", {
          modelType: "Task",
          recordId: createdTask.id,
          entityType: createdTask.EntityType,
          entityID: createdTask.EntityID,
          serviceType: createdTask.ServiceType,
          changeAction: "Created",
          user: {
            id: currentUser.id,
            name: currentUser.Name,
            imageThumbnail: currentUser.ImageThumbnail,
          },
        });
      }

      setTasks((prevTasks) => {
        const tasksByStatus = groupTasksByStatus(prevTasks);
        const statusTasks = tasksByStatus[status] || [];
        tasksByStatus[status] = [...statusTasks, createdTask];
        return flattenTasksByStatus(tasksByStatus);
      });
    } catch (error) {
      console.error("Error creating task:", error);
      handleErrorDialog("Failed to create task", theme);
    }
  };

  // Render a single task card using TaskItem component
  const renderTaskCard = (task, index) => {
    return (
      <Draggable
        draggableId={`task-${task.id}`}
        index={index}
        key={task.id}
        disableInteractiveElementBlocking
      >
        {(provided, snapshot) => (
          <TaskItem
            key={task.id}
            task={task}
            onClick={() => handleTaskClick(task)}
            onContextMenu={(e) => handleContextMenu(e, task)}
            provided={provided}
            snapshot={snapshot}
          />
        )}
      </Draggable>
    );
  };

  return (
    <Box
      p="1rem 1rem 0 1rem"
      bgcolor={theme.palette.background.alt}
      sx={{
        flex: 1,
        position: "relative",
        display: "flex",
        flexDirection: "column",
        height: `calc(100vh - ${NAVBAR_HEIGHT + 20}px)`,
      }}
    >
      <Box>
        <Grid container alignItems="center">
          <Grid
            size={{ xs: 12 }}
            container
            justifyContent="flex-end"
            sx={{ px: 1 }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
              gap={2}
            >
              <Box
                sx={{
                  flex: isMobile ? 0 : 1,
                  maxWidth: isMobile ? undefined : FILTER_SELECTOR_WIDTH,
                }}
              >
                <TasksFilterSelector
                  label="Filters"
                  priorities={priorities}
                  entityServiceTypes={entityServiceTypes}
                  selectedDueDates={filters.dueDates}
                  selectedPriorities={filters.priorities}
                  selectedEntityServiceTypes={filters.entityServiceTypes}
                  showAllCompleted={filters.showAllCompleted}
                  showAutomatedTasks={filters.showAutomatedTasks}
                  selectedMember={filters.selectedMember}
                  currentUserId={currentUser?.id}
                  onSave={handleFiltersChange}
                  iconOnly={isMobile}
                />
              </Box>
              <Box display="flex" gap={2} alignItems="center">
                {!isMobile ? (
                  <>
                    <Button
                      onClick={() => {
                        setOverlayData({});
                        setOverlayName(OVERLAY_NAMES.TASKS_AUTOMATED);
                      }}
                      startIcon={<AutoAwesomeRoundedIcon />}
                      sx={{
                        width: "200px",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        padding: "8px 16px",
                        backgroundColor: theme.palette.background.default,
                        borderRadius: "12px",
                        boxShadow:
                          "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                        color: theme.palette.primary.main,
                        textTransform: "none",
                        fontSize: "0.875rem",
                        "& .MuiButton-startIcon": {
                          marginRight: 0,
                        },
                        "& .MuiButton-endIcon": {
                          display: "none",
                        },
                        "&:hover": {
                          backgroundColor: theme.palette.primary.buttonHover,
                          boxShadow:
                            "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                        },
                      }}
                    >
                      <Box sx={{ flex: 1, textAlign: "center" }}>
                        Automated Tasks
                      </Box>
                    </Button>
                    <Button
                      onClick={() => {
                        setOverlayData({
                          Action: OVERLAY_NAMES.TASK_ACTION_NEW,
                          Task: null,
                        });
                        setOverlayName(OVERLAY_NAMES.TASK_NEW_EDIT);
                      }}
                      startIcon={<AddRoundedIcon />}
                      sx={{
                        width: "200px",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        padding: "8px 16px",
                        backgroundColor: theme.palette.primary.main,
                        borderRadius: "12px",
                        boxShadow:
                          "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                        color: theme.palette.background.default,
                        textTransform: "none",
                        fontSize: "0.875rem",
                        "& .MuiButton-startIcon": {
                          marginRight: 0,
                        },
                        "& .MuiButton-endIcon": {
                          display: "none",
                        },
                        "&:hover": {
                          backgroundColor:
                            theme.palette.primary.buttonHoverSelected,
                          color: theme.palette.background.default,
                          boxShadow:
                            "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                        },
                      }}
                    >
                      <Box sx={{ flex: 1, textAlign: "center" }}>Add Task</Box>
                    </Button>
                  </>
                ) : (
                  <>
                    <Tooltip title="Automated Tasks">
                      <IconButton
                        onClick={() => {
                          setOverlayData({});
                          setOverlayName(OVERLAY_NAMES.TASKS_AUTOMATED);
                        }}
                        sx={{
                          width: "40px",
                          height: "40px",
                          backgroundColor: theme.palette.background.default,
                          boxShadow:
                            "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                          borderRadius: "12px",
                          border: "none",
                          color: theme.palette.primary.main,
                          "&:hover": {
                            backgroundColor: theme.palette.primary.buttonHover,
                            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                          },
                        }}
                      >
                        <AutoAwesomeRoundedIcon />
                      </IconButton>
                    </Tooltip>
                    <Button
                      onClick={() => {
                        setOverlayData({
                          Action: OVERLAY_NAMES.TASK_ACTION_NEW,
                          Task: null,
                        });
                        setOverlayName(OVERLAY_NAMES.TASK_NEW_EDIT);
                      }}
                      sx={{
                        width: "40px",
                        minWidth: "40px",
                        height: "40px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 0,
                        backgroundColor: theme.palette.primary.main,
                        borderRadius: "12px",
                        boxShadow:
                          "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                        color: theme.palette.background.default,
                        textTransform: "none",
                        fontSize: "0.875rem",
                        "& .MuiButton-startIcon": {
                          margin: 0,
                        },
                        "& .MuiButton-endIcon": {
                          display: "none",
                        },
                        "&:hover": {
                          backgroundColor:
                            theme.palette.primary.buttonHoverSelected,
                          color: theme.palette.background.default,
                          boxShadow:
                            "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                        },
                      }}
                    >
                      <AddRoundedIcon />
                    </Button>
                  </>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <TaskBoard
        statuses={statuses}
        tasks={tasks}
        isLoading={isLoading}
        onDragEnd={handleDragEnd}
        renderTaskCard={renderTaskCard}
        creatingTaskForStatus={creatingTaskForStatus}
        newTaskTitle={newTaskTitle}
        setNewTaskTitle={setNewTaskTitle}
        handleCreateInlineTask={handleCreateInlineTask}
        setCreatingTaskForStatus={setCreatingTaskForStatus}
        containerPadding={"0.75rem 0.5rem"}
        theme={theme}
      />

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        slotProps={{
          paper: {
            sx: {
              bgcolor: theme.palette.background.alt,
              border: `1px solid ${theme.palette.divider}`,
              "& .MuiListItemIcon-root": {
                color: theme.palette.primary.main,
              },
            },
          },
        }}
      >
        <MenuItem onClick={handleEditFromContext}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText sx={{ color: theme.palette.primary.text }}>
            Edit
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteTask}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText sx={{ color: theme.palette.primary.text }}>
            Delete
          </ListItemText>
        </MenuItem>
        {selectedTaskForContext?.EntityType &&
          selectedTaskForContext?.EntityID && (
            <MenuItem onClick={handleOpenEntityProfile}>
              <ListItemIcon>
                <OpenInNewIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText sx={{ color: theme.palette.primary.text }}>
                Open {getEntityLabel(selectedTaskForContext.EntityType)} Profile
              </ListItemText>
            </MenuItem>
          )}
      </Menu>
    </Box>
  );
};

export default Tasks;
